import { DateTime, Duration } from 'luxon';
import { unitsFormatter, round } from '@deckee/utilities';
import {
  METRE_TO_NAUTICAL_MILE_FACTOR,
  METRE_PER_SECOND_TO_KNOT_FACTOR,
} from './constants';

/**
 * Date formatting functions
 */

export const formatDataValue = (
  value,
  prefix = '',
  suffix = '',
  emptyString = '',
) => {
  // Need to allow for '0' values ie. no wind or rain etc.
  if (value || value === 0) {
    return prefix + value + suffix;
  }

  return emptyString;
};

export const formatTemperature = (
  temperature,
  unitsConfig,
  returnRawValue = false,
  returnRoundedInt = false,
) => {
  if (temperature === null || typeof temperature === 'undefined') return; // This should return a value. Perhaps a `null`. The below eslint-disable is because of this.

  let formattedTemperature = parseFloat(temperature);

  if (
    formattedTemperature !== null ||
    typeof formattedTemperature !== 'undefined'
  ) {
    formattedTemperature = unitsFormatter(
      formattedTemperature,
      'temperature',
      unitsConfig,
      '',
      returnRawValue,
    );
  }

  if (formattedTemperature && returnRoundedInt) {
    formattedTemperature = Math.round(formattedTemperature);
  }
  // eslint-disable-next-line consistent-return
  return formattedTemperature;
};

/**
 * Format distances consistently for displaying on the frontend
 *
 * @param {number} distanceInMetres - distance value to format in meters - (6525.69755)
 * @return Formatted distance object { value: number, unit: string}
 */
export const formatDistanceDisplay = (distanceInMetres, unitsConfig) => {
  const distanceSetting = unitsConfig?.distance;
  const displayInKilometres = distanceSetting === 'kilometre';

  const formattedDistance = {
    value: 0, // default value for zero or an invalid distance
    unit: displayInKilometres ? 'KM' : 'NM', // default display unit
  };

  if (distanceInMetres == null) return formattedDistance;

  // Convert metre(m - SI unit) to either kilometres or the default of nautical miles (NM)
  const converted =
    distanceInMetres *
    (displayInKilometres ? 0.001 : METRE_TO_NAUTICAL_MILE_FACTOR);

  // Round to the required number of places
  const rounded = round(converted, 2);

  // Check we have a valid distance
  //  note: distance cannot be negative, and never decreases
  if (rounded && rounded > 0) {
    formattedDistance.value = rounded;
  }

  return formattedDistance;
};

/**
 * Format a distance in metres as a compact "away" label, honouring the user's
 * distance unit preference (kilometres or nautical miles).
 *
 * @param {number} distanceInMetres
 * @param {object} unitsConfig
 * @return {string} e.g. "2.5km away" or "1.35nm away"
 */
export const formatDistanceAwayLabel = (distanceInMetres, unitsConfig) => {
  if (distanceInMetres == null || Number.isNaN(distanceInMetres)) {
    return '';
  }

  const { value, unit } = formatDistanceDisplay(distanceInMetres, unitsConfig);

  if (!value || value <= 0) {
    return '';
  }

  const formattedValue = value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return `${formattedValue}${unit.toLowerCase()} away`;
};

/**
 * Format speed consistently for displaying on the frontend
 *
 * @param {number} speedInMps - speed value to format in meters per second - (1.3796875)
 * @return {Object} Formatted speed object { value: number, unit: string}
 */
export const formatSpeedDisplay = (speedInMps, unitsConfig) => {
  const speedSetting = unitsConfig?.speed;
  const displayInKilometrePerHour = speedSetting === 'kilometrePerHour';

  const formattedSpeed = {
    value: 0, // default value for zero or an invalid distance
    unit: displayInKilometrePerHour ? 'KM/H' : 'KN', // default display unit
  };

  if (speedInMps == null) return formattedSpeed;

  // Convert metre(m - SI unit) to either KM/H or the display default of nautical miles (NM)
  const converted =
    speedInMps *
    (displayInKilometrePerHour ? 3.6 : METRE_PER_SECOND_TO_KNOT_FACTOR);

  // Round to the required number of places
  const rounded = round(converted, 1);

  // Check we have a valid distance
  //  note: distance cannot be negative, and never decreases
  if (rounded && rounded > 0) {
    formattedSpeed.value = +rounded.toFixed(1);
  }

  return formattedSpeed;
};

/**
 * Format speed consistently for displaying on the frontend
 *
 * @param {number} heading - heading value to format degrees from true north - (23.564)
 * @return {string} Formatted heading string
 */
export const formatHeading = (heading) => {
  let formattedHeading = '-';

  // Format the heading if it is valid
  if (!Number.isNaN(parseFloat(heading))) {
    formattedHeading = parseFloat(heading).toFixed(1);
  }

  // Add units
  formattedHeading = `${formattedHeading} \u00B0`;

  return formattedHeading;
};

/**
 * Format time duration consistently for displaying on the frontend
 *
 * @param {string|null} utcStartTimestamp - UTC start timestamp to calculate time from (ISO)
 * @param {string|null} utcEndTimestamp - UTC end timestamp to calculate time to (ISO)
 * @param {number|null} timeDuration - Supplied time duration in milliseconds
 *  NOTE: If using timeDuration then start and end times will be ignored
 *  ie. the time duration will be: (utcEndTimestamp - utcStartTimestamp) or (supplied timeDuration)
 * @return Formatted display time object containing the formatted values
 */
export const formatTimeDurationDisplay = (
  utcStartTimestamp = null,
  utcEndTimestamp = null,
  timeDuration = null,
) => {
  const formattedTime = {};
  let totalMilliseconds = timeDuration;

  // Calculate time duration  from start and end times if not supplied
  if (!timeDuration && utcStartTimestamp && utcEndTimestamp) {
    const startTimeObj = DateTime.fromISO(utcStartTimestamp, { zone: 'utc' });
    const endTimeObj = DateTime.fromISO(utcEndTimestamp, { zone: 'utc' });

    // Check we have valid start and end times
    if (startTimeObj.isValid && endTimeObj.isValid) {
      totalMilliseconds = endTimeObj.ts - startTimeObj.ts;
    } else totalMilliseconds = 0;
  }

  // Format the time duration
  let totalTime = null;
  switch (true) {
    // 0. less than 1 sec - display zero seconds - 0s
    case totalMilliseconds < 60:
      formattedTime.seconds = { value: 0, unit: 'S' };
      break;

    // 1. less than 1 minute - display seconds - eg. 43s
    case totalMilliseconds < 60000:
      totalTime = Duration.fromMillis(totalMilliseconds)
        .shiftTo('seconds')
        .normalize()
        .toObject();
      formattedTime.seconds = { value: round(totalTime.seconds, 0), unit: 'S' };
      break;

    // 2. less than 1 hour - display minutes & seconds — eg. 39m 12s
    case totalMilliseconds < 3600000:
      totalTime = Duration.fromMillis(totalMilliseconds)
        .shiftTo('minutes', 'seconds')
        .normalize()
        .toObject();
      formattedTime.minutes = { value: round(totalTime.minutes, 0), unit: 'M' };
      formattedTime.seconds = { value: round(totalTime.seconds, 0), unit: 'S' };
      break;

    // 3. less than 3 days - display hours & minutes — eg. 37h 9m
    case totalMilliseconds < 259200000:
      totalTime = Duration.fromMillis(totalMilliseconds)
        .shiftTo('hours', 'minutes')
        .normalize()
        .toObject();
      formattedTime.hours = { value: round(totalTime.hours, 0), unit: 'H' };
      formattedTime.minutes = { value: round(totalTime.minutes, 0), unit: 'M' };
      break;

    // 4. less than 100 days - display days & hours — eg. 56d 12h
    case totalMilliseconds < 8640000000:
      totalTime = Duration.fromMillis(totalMilliseconds)
        .shiftTo('days', 'hours')
        .normalize()
        .toObject();
      formattedTime.days = { value: round(totalTime.days, 0), unit: 'D' };
      formattedTime.hours = { value: round(totalTime.hours, 0), unit: 'H' };
      break;

    // 5. over 100 days - display days — eg. 273d
    default:
      totalTime = Duration.fromMillis(totalMilliseconds)
        .shiftTo('days')
        .normalize()
        .toObject();
      formattedTime.days = { value: round(totalTime.days, 0), unit: 'D' };
      break;
  }

  return formattedTime;
};

/**
 * Format times consistently for displaying on the frontend
 *
 * @param {string} isoTimestamp - ISO timestamp to format ('2017-04-20T11:32:00.000-04:00')
 * @param {boolean} returnUtcTime - format the time in UTC otherwise default to local time
 * @return {Object} Formatted display time object containing the formatted values
 */
export const formatTimeDisplay = (isoTimestamp, returnUtcTime = false) => {
  let timeObj = null;
  let formattedTime = { time: '-' };

  // Format the time
  if (returnUtcTime) {
    timeObj = DateTime.fromISO(isoTimestamp, { zone: 'utc' });
  } else {
    timeObj = DateTime.fromISO(isoTimestamp);
  }

  // Check the supplied time is valid
  if (timeObj.isValid) {
    const time = timeObj.toFormat('h:mm');
    const meridiem = timeObj.toFormat('a');
    formattedTime = { time, meridiem };
  }

  return formattedTime;
};
